// @flow

/**
 * Module dependencies.
 */

import { Col, Row } from 'client/components/core/grid';
import { ErrorMessage, SuccessMessage } from 'client/components/forms/components/messages';
import { Input } from 'client/components/core/forms/input';
import { LoadingButton } from 'client/components/core/buttons';
import { Select, Textarea } from 'client/components/core/forms';
import { colors, defaultTransition, units } from 'client/styles';
import { isEmail } from 'client/core/utils';
import { pick } from 'lodash';
import { translator } from 'core/utils';
import Layout from 'client/components/core/layout';
import React, { Component } from 'react';
import Text from 'client/components/core/type';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  fullfilled: boolean,
  onReset: Function,
  onSubmit: Function,
  pending: boolean,
  selectOptions: Array<Object>,
  selectedOption: number | string,
  submissionError: boolean,
  translate: Function
};

/**
 * `FormContainer` component.
 */

const FormContainer = styled.div`
  background-color: ${colors.white};
  position: relative;
  transition: ${defaultTransition};
  transition-property: opacity, transform;

  ${({ visible }: Object): string => `
    opacity: ${visible ? 1 : 0};
    transition-delay: ${visible ? 0 : 0.35}s;
    pointer-events: ${visible ? 'all' : 'none'};
    transform: translate3d(0, ${visible ? '0' : '-40px'}, 0);
  `}
`;

/**
 * Export `ContactForm`.
 */

class ContactForm extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * Initial state.
   */

  initialState: Object = {
    description: '',
    email: '',
    reason: this.props.selectedOption
  };

  /**
   * State.
   */

  state: Object = {
    ...this.initialState
  };

  /**
   * Form fields.
   */

  formFields = ['description', 'email', 'reason'];

  /**
   * Reset form fields.
   */

  resetForm = (event: any) => {
    event.preventDefault();

    this.setState(this.initialState, this.props.onReset());
  }

  /**
   * Is form valid.
   */

  isFormValid = (): boolean => {
    const { description, email, reason } = this.state;

    return !description.length || !email.length || !isEmail(email) || !reason;
  }

  /**
   * Handle form submit.
   */

  handleSubmit = (event: any) => {
    event.preventDefault();

    if (this.isFormValid()) {
      return;
    }

    const formData = pick(this.state, this.formFields);

    this.props.onSubmit(formData);
  };

  /**
   * Handle input change.
   */

  handleInputChange = ({ target }: any) => {
    const { value, name } = target;

    this.setState({
      [name]: value
    });
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const {
      fullfilled,
      pending,
      selectOptions,
      submissionError,
      translate
    } = this.props;

    const successContent = {
      button: translate('formRequest.message.success.button'),
      message: translate('formRequest.message.success.message'),
      title: translate('formRequest.message.success.title')
    };

    return (
      <Layout position={'relative'}>
        <Row>
          <Col
            lg={2}
            md={3}
            xs={10}
            xsOffset={1}
          >
            <Layout
              marginBottom={units(6)}
              marginBottomMd={units(6)}
            >
              <Text.H2
                color={colors.brandPrimary}
                weight={700}
              >
                {translate('contacts.form.title')}
              </Text.H2>
            </Layout>
          </Col>

          <Col
            md={5}
            xs={10}
            xsOffset={1}
          >
            <Layout paddingTopMd={units(2)}>
              <form onSubmit={this.handleSubmit}>
                <FormContainer visible={!pending && !fullfilled}>
                  <Select
                    id={'contact-subject'}
                    label={translate('contacts.form.subject')}
                    name={'reason'}
                    onUpdate={this.handleInputChange}
                    options={selectOptions}
                    value={this.state.reason}
                  />

                  <Input
                    id={'contact-email'}
                    label={translate('contacts.form.email')}
                    name={'email'}
                    onUpdate={this.handleInputChange}
                    validation={isEmail}
                    value={this.state.email}
                  />

                  <Textarea
                    expandActive
                    id={'contact-message'}
                    label={translate('contacts.form.message')}
                    name={'description'}
                    onUpdate={this.handleInputChange}
                    type={'textarea'}
                    value={this.state.description}
                  />
                </FormContainer>

                <Layout
                  paddingTop={units(4)}
                  position={'relative'}
                  textAlign={'center'}
                  zIndex={3}
                >
                  <LoadingButton
                    disabled={this.isFormValid()}
                    loading={pending}
                    type={'submit'}
                  >
                    {translate('contacts.form.submit')}
                  </LoadingButton>
                </Layout>
              </form>

              <ErrorMessage
                message={translate('formRequest.message.error')}
                visible={submissionError}
              />

              <SuccessMessage
                button={successContent.button}
                buttonAction={this.resetForm}
                message={successContent.message}
                title={successContent.title}
                visible={fullfilled}
              />
            </Layout>
          </Col>
        </Row>
      </Layout>
    );
  }

}

/**
 * Export `ContactForm` component.
 */

export default translator()(ContactForm);



// WEBPACK FOOTER //
// ../src/client/components/contacts/contact-form.js